import type { VideoSource } from '../../../../shared/protocol';
import { parseYouTubeUrl } from './providers/youtube/youtubeUrlParser';

export type VideoSourceDetection =
  | { kind: 'supported'; source: VideoSource }
  | { kind: 'unsupported'; reason: string };

const TWITCH_RESERVED_PATHS = new Set([
  'directory', 'downloads', 'jobs', 'p', 'settings', 'subscriptions', 'wallet',
]);

export function detectVideoSource(input: string): VideoSourceDetection {
  const value = input.trim();
  if (!value) return { kind: 'unsupported', reason: 'Cole um link HTTPS.' };

  if (/^https?:\/\//i.test(value)) {
    try {
      const explicitUrl = new URL(value);
      if (explicitUrl.protocol !== 'https:' || explicitUrl.username || explicitUrl.password) {
        return { kind: 'unsupported', reason: 'Por segurança, são aceitas apenas URLs HTTPS sem credenciais.' };
      }
    } catch {
      return { kind: 'unsupported', reason: 'Use uma URL HTTPS válida.' };
    }
  }

  const youtube = parseYouTubeUrl(value);
  if (youtube) return { kind: 'supported', source: youtube };

  let url: URL;
  try { url = new URL(value); } catch {
    return { kind: 'unsupported', reason: 'Use uma URL HTTPS completa.' };
  }
  if (url.protocol !== 'https:' || url.username || url.password) {
    return { kind: 'unsupported', reason: 'Por segurança, são aceitas apenas URLs HTTPS sem credenciais.' };
  }
  if (url.toString().length > 2048) {
    return { kind: 'unsupported', reason: 'A URL excede o limite de 2048 caracteres.' };
  }

  const twitch = detectTwitch(url);
  if (twitch) return { kind: 'supported', source: twitch };

  const path = url.pathname.toLowerCase();
  if (/\.(mp4|webm|ogv|ogg|m4v)$/.test(path)) {
    url.hash = '';
    return { kind: 'supported', source: { provider: 'direct', mediaUrl: url.toString() } };
  }
  if (/\.m3u8$/.test(path)) {
    url.hash = '';
    return { kind: 'supported', source: { provider: 'hls', mediaUrl: url.toString() } };
  }

  return { kind: 'supported', source: { provider: 'iframe', mediaUrl: url.toString() } };
}

function detectTwitch(url: URL): VideoSource | null {
  const host = url.hostname.toLowerCase().replace(/^www\./, '');
  const parts = url.pathname.split('/').filter(Boolean);
  if (host === 'clips.twitch.tv') {
    const slug = parts[0]?.toLowerCase() === 'embed' ? url.searchParams.get('clip') : parts[0] ?? url.searchParams.get('clip');
    return slug && isTwitchToken(slug) ? { provider: 'twitch', kind: 'clip', id: slug } : null;
  }
  if (host !== 'twitch.tv') return null;

  if (parts[0]?.toLowerCase() === 'videos' && /^\d{1,20}$/.test(parts[1] ?? '')) {
    return { provider: 'twitch', kind: 'vod', id: parts[1] };
  }
  if ((parts.length === 3 && parts[1].toLowerCase() === 'clip' || parts.length === 2 && parts[0].toLowerCase() === 'clip') && isTwitchToken(parts.at(-1) ?? '')) {
    return { provider: 'twitch', kind: 'clip', id: parts.at(-1) as string };
  }
  if (parts.length === 1 && isTwitchToken(parts[0]) && !TWITCH_RESERVED_PATHS.has(parts[0].toLowerCase())) {
    return { provider: 'twitch', kind: 'channel', id: parts[0] };
  }
  return null;
}

function isTwitchToken(value: string): boolean {
  return /^[A-Za-z0-9_-]{1,100}$/.test(value);
}
